import { useState } from "react";
import { Spinner } from "../util-cmps/spinner";
import { RequiredAsterisk } from "./required-asterisk";

export interface ExistingControl {
    status: "new" | "loading" | "fetched"
    controlId: number | null
    consolidationJson?: string | null
}

interface ExistingControlProps {
    existingControl: ExistingControl
    onExistingControlOverride: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const InfoIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
)

const TOOLTIP_LINES = [
    'A Control is the combination of objective + systems (not visible in the backoffice).',
    'An Audit Control links a Control to an audit year.',
    'Using an existing control reuses the same configuration (systems, consolidation) for the new year.',
    '',
    '⚠ Changing the control configuration (consolidation, systems) will also affect older audit controls from previous years that share this control.',
]

export const ExistingControlSection: React.FC<ExistingControlProps> = ({ existingControl, onExistingControlOverride }) => {
    const [showTooltip, setShowTooltip] = useState(false)

    const isExistingControl = existingControl.status === 'fetched' && existingControl.controlId

    if (existingControl.status === 'new') return null

    if (existingControl.status === 'loading') return <>
        <span></span>
        <span className="span-2">
            <Spinner isSmall={true} className="justify-start" />
        </span>
    </>

    if (!isExistingControl) return <>
        <span></span>
        <span className="span-2 form-info">No existing control found — a new one will be created.</span>
    </>


    return <div className="span-3" style={{ paddingTop: '1rem', borderTop: '1px solid #e2e8f0', marginTop: '1rem' }}>
        <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginBottom: '0.35rem' }}>
            Existing Control
            <RequiredAsterisk />
            <span
                onClick={() => setShowTooltip(!showTooltip)}
                style={{ cursor: 'pointer', marginLeft: '0.3rem', verticalAlign: 'middle', color: '#3b82f6' }}
            >
                <InfoIcon />
            </span>
            {showTooltip && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '0.4rem',
                    padding: '0.75rem 1rem',
                    background: '#1e293b',
                    color: '#f1f5f9',
                    fontSize: '0.75rem',
                    lineHeight: '1.5',
                    borderRadius: '8px',
                    width: '340px',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}>
                    {TOOLTIP_LINES.map((line, i) => line === ''
                        ? <br key={i} />
                        : <p key={i} style={{ margin: '0 0 0.3rem 0' }}>{line}</p>
                    )}
                </div>
            )}
        </label>
        <div className="existing-control">
            <span style={{ display: 'block', marginBottom: '0.2rem' }}>Found control ID: {existingControl.controlId}</span>
            <ul className="clean-list">
                <li>
                    <input type="radio" name="override-control" id="override-yes" defaultChecked onChange={onExistingControlOverride} />
                    <label htmlFor="override-yes" className="font-normal">Use existing control</label>
                </li>
                <li>
                    <input type="radio" name="override-control" id="override-no" onChange={onExistingControlOverride} />
                    <label htmlFor="override-no" className="font-normal">Create new control</label>
                </li>
            </ul>
        </div>
    </div>
}
